import { Quaternion, Spherical } from 'three';
import type { CameraState } from '@/lib/threeScene/camera';
import { ORBIT_AXIS, WORLD_UP } from '@/lib/threeScene/orbitAxis';

/** Don't let the camera reach the top pole — keep 5° of margin. */
const MIN_PHI = (5 * Math.PI) / 180;
/** Don't let the camera reach the bottom pole — keep 5° of margin. */
const MAX_PHI = (175 * Math.PI) / 180;

/**
 * Rotate the camera around its target by `dTheta` (azimuth, in radians)
 * and `dPhi` (elevation, in radians) in spherical coordinates around
 * `ORBIT_AXIS` (LPS.Superior — the patient's spine). Preserves
 * camera-to-target distance. Clamps elevation to keep a small margin from
 * the poles (`MIN_PHI` / `MAX_PHI`) so `lookAt` stays well-conditioned
 * and orbit doesn't gimbal-lock.
 *
 * Use this for any incremental camera-orbit operation — keyboard
 * shortcuts, mouse drag-to-rotate, programmatic nudges, etc. Mutates
 * `camera.position` in place; `camera.target` and `camera.up` are
 * untouched.
 */
export function nudgeCamera(camera: CameraState, dTheta: number, dPhi: number): void {
    const offset = camera.position.clone().sub(camera.target);
    const radius = offset.length();
    if (radius < 1e-6) return;

    const quat = new Quaternion().setFromUnitVectors(ORBIT_AXIS, WORLD_UP);
    offset.applyQuaternion(quat);

    const spherical = new Spherical().setFromVector3(offset);
    spherical.theta += dTheta;
    spherical.phi = Math.max(MIN_PHI, Math.min(MAX_PHI, spherical.phi + dPhi));
    spherical.radius = radius;

    offset.setFromSpherical(spherical);
    offset.applyQuaternion(quat.invert());

    camera.position.copy(camera.target.clone().add(offset));
}
